import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import {
  TrendingUp, Users, MapPin, AlertTriangle, DollarSign,
  Eye, Award, Target, ChevronRight, BarChart3, Activity, Bell
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { intelligenceAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const OwnerIntelligenceDashboard = () => {
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchOverview();
    }
  }, [activeBusiness?.id]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const { data } = await intelligenceAPI.getOverview({
        date_str: new Date().toISOString().split('T')[0]
      });
      setOverview(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load intelligence overview'));
    } finally {
      setLoading(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      CRITICAL: 'bg-red-50 text-red-700',
      WARN: 'bg-amber-50 text-amber-700',
      INFO: 'bg-blue-50 text-blue-700',
    };
    return styles[severity] || 'bg-slate-50 text-slate-700';
  };

  const visitCompletion = overview?.total_visits_planned_today > 0
    ? Math.round((overview.total_visits_completed_today / overview.total_visits_planned_today) * 100)
    : 0;

  return (
    <div className="h-full flex flex-col" data-testid="intelligence-overview-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Intelligence Overview"
        subtitle="Real-time insights into field operations and staff performance"
        stats={[
          { icon: Target, label: 'Visits Today', value: `${overview?.total_visits_completed_today || 0}/${overview?.total_visits_planned_today || 0}`, color: 'slate' },
          { icon: Activity, label: 'Completion', value: `${visitCompletion}%`, color: 'emerald' },
          { icon: MapPin, label: 'Distance', value: `${overview?.total_distance_today?.toFixed(1) || 0} km`, color: 'blue' },
          { icon: DollarSign, label: 'Collections', value: formatCompactCurrency(overview?.total_collections_today), color: 'purple' },
          { icon: AlertTriangle, label: 'Open Alerts', value: overview?.open_alerts_count || 0, color: overview?.open_alerts_count > 0 ? 'rose' : 'slate' },
        ]}
        primaryAction={{
          label: 'Staff Insights',
          icon: BarChart3,
          onClick: () => navigate(`/business/${activeBusiness?.id}/intelligence/staff`)
        }}
        secondaryAction={{
          label: 'View Alerts',
          icon: Bell,
          onClick: () => navigate(`/business/${activeBusiness?.id}/intelligence/alerts`)
        }}
      />

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {/* Top Performers Table */}
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden mb-3">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading intelligence data...</p>
              </div>
            </div>
          ) : !overview || (!overview.top_staff?.length && !overview.bottom_staff?.length) ? (
            <div className="flex-1 flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Eye className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No intelligence data available</p>
              <p className="text-[13px] text-slate-400 mt-1">Performance data will appear once field activities are recorded</p>
            </div>
          ) : (
            <>
              {/* Top Performers Section */}
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50/50 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Award className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800">Top Performers</h3>
                    <p className="text-[11px] text-slate-500">Best performing staff members today</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/business/${activeBusiness?.id}/intelligence/staff`)}
                  className="text-[12px] text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                >
                  View All
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <table className="tl-table">
                <thead>
                  <tr>
                    <th className="tl-th tl-th-teal" style={{ width: '30%' }}>Staff Member</th>
                    <th className="tl-th tl-th-teal" style={{ width: '18%' }}>Performance</th>
                    <th className="tl-th tl-th-teal" style={{ width: '18%' }}>Visits</th>
                    <th className="tl-th tl-th-teal" style={{ width: '18%' }}>Collections</th>
                    <th className="tl-th tl-th-teal" style={{ width: '16%' }}>Score</th>
                  </tr>
                </thead>
                <tbody className="animate-stagger">
                  {overview.top_staff?.map((staff, idx) => (
                    <tr
                      key={staff.id}
                      className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn cursor-pointer ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                      style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                      onClick={() => navigate(`/business/${activeBusiness?.id}/intelligence/staff`)}
                    >
                      <td className="tl-td">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold ${getInitialColor(staff.staff_name)}`}>
                            {getInitials(staff.staff_name)}
                          </div>
                          <div>
                            <p className="text-[13px] font-semibold text-slate-800">{staff.staff_name}</p>
                            <p className="text-[11px] text-slate-500">Rank #{idx + 1}</p>
                          </div>
                        </div>
                      </td>
                      <td className="tl-td">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md text-[11px] font-medium">
                          <TrendingUp className="w-3 h-3" />
                          Excellent
                        </span>
                      </td>
                      <td className="tl-td">
                        <div className="text-[13px] font-semibold text-slate-800">
                          {staff.total_visits_completed}/{staff.total_visits_planned}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {staff.total_visits_planned > 0 ? Math.round((staff.total_visits_completed / staff.total_visits_planned) * 100) : 0}% complete
                        </div>
                      </td>
                      <td className="tl-td">
                        <div className="flex items-center gap-1.5 text-[13px] text-slate-600">
                          <DollarSign className="w-3.5 h-3.5 text-slate-400" />
                          {formatCompactCurrency(staff.total_collections_amount)}
                        </div>
                      </td>
                      <td className="tl-td">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[11px] font-medium">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                          {staff.score_overall?.toFixed(0) || 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Needs Attention Table */}
        {overview?.bottom_staff?.length > 0 && (
          <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden mb-3">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50/50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-[14px] font-semibold text-slate-800">Needs Attention</h3>
                  <p className="text-[11px] text-slate-500">Staff members requiring follow-up</p>
                </div>
              </div>
            </div>
            <table className="tl-table">
              <thead>
                <tr>
                  <th className="tl-th tl-th-teal" style={{ width: '30%' }}>Staff Member</th>
                  <th className="tl-th tl-th-teal" style={{ width: '18%' }}>Performance</th>
                  <th className="tl-th tl-th-teal" style={{ width: '18%' }}>Visits</th>
                  <th className="tl-th tl-th-teal" style={{ width: '18%' }}>Collections</th>
                  <th className="tl-th tl-th-teal" style={{ width: '16%' }}>Score</th>
                </tr>
              </thead>
              <tbody className="animate-stagger">
                {overview.bottom_staff?.map((staff, idx) => (
                  <tr
                    key={staff.id}
                    className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn cursor-pointer ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                    style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                    onClick={() => navigate(`/business/${activeBusiness?.id}/intelligence/staff`)}
                  >
                    <td className="tl-td">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold ${getInitialColor(staff.staff_name)}`}>
                          {getInitials(staff.staff_name)}
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-slate-800">{staff.staff_name}</p>
                          <p className="text-[11px] text-slate-500">Needs review</p>
                        </div>
                      </div>
                    </td>
                    <td className="tl-td">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-rose-50 text-rose-700 rounded-md text-[11px] font-medium">
                        <AlertTriangle className="w-3 h-3" />
                        Below Target
                      </span>
                    </td>
                    <td className="tl-td">
                      <div className="text-[13px] font-semibold text-slate-800">
                        {staff.total_visits_completed}/{staff.total_visits_planned}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {staff.total_visits_planned > 0 ? Math.round((staff.total_visits_completed / staff.total_visits_planned) * 100) : 0}% complete
                      </div>
                    </td>
                    <td className="tl-td">
                      <div className="flex items-center gap-1.5 text-[13px] text-slate-600">
                        <DollarSign className="w-3.5 h-3.5 text-slate-400" />
                        {formatCompactCurrency(staff.total_collections_amount)}
                      </div>
                    </td>
                    <td className="tl-td">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-rose-700 rounded-full text-[11px] font-medium">
                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full"></span>
                        {staff.score_overall?.toFixed(0) || 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Recent Alerts */}
        {overview?.recent_alerts?.length > 0 && (
          <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden mb-3">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-rose-50/50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
                  <Bell className="w-4 h-4 text-rose-600" />
                </div>
                <div>
                  <h3 className="text-[14px] font-semibold text-slate-800">Recent Alerts</h3>
                  <p className="text-[11px] text-slate-500">Latest intelligence alerts</p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/business/${activeBusiness?.id}/intelligence/alerts`)}
                className="text-[12px] text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
              >
                View All
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="divide-y divide-slate-100">
              {overview.recent_alerts.map((alert) => (
                <div key={alert.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50/50">
                  <div className={`mt-0.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${getSeverityBadge(alert.severity)}`}>
                    {alert.severity}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-slate-800">{alert.title}</p>
                    <p className="text-[12px] text-slate-500 mt-0.5 truncate">{alert.message}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {alert.entity_name && (
                        <span className="text-[11px] text-slate-400">{alert.entity_name}</span>
                      )}
                      {alert.created_at && (
                        <span className="text-[11px] text-slate-400">
                          {new Date(alert.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        {overview && (overview.top_staff?.length > 0 || overview.bottom_staff?.length > 0) && (
          <div className="flex items-center justify-between mt-3 px-1">
            <p className="text-[12px] text-slate-500">
              Last updated: <span className="font-medium text-slate-700">{new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
            </p>
            <p className="text-[11px] text-slate-400">Click on a row to view staff insights</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerIntelligenceDashboard;
